import { ObjectRepository, RefBuilder, ServerValue } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type {
  FolderId,
  UserId,
  ViewModelId,
  WorkspaceId,
  SharingUserRoleJSON,
  ViewModelIndividualPermissionRole,
} from 'balus-common';
import { ViewModelClipboardOperator } from '@view-model/application/clipboard';
import { ViewModelClipboardPayload } from '@view-model/application/operation';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ViewModelCascadeRepository';
import { Workspace } from '@workspace/domain/workspace';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { RootFolderTreeRepository } from '@workspace/view-model-folder/infrastructure/FolderTree';

type CreateLogSender = (
  eventName: 'view_model:create',
  params: { viewModelId: ViewModelId; viewModelName: string }
) => void;

type UpdateLogSender = (
  eventName: 'view_model:edit',
  params: {
    viewModelId: ViewModelId;
    viewModelName: string;
    viewModelShareSetting: string;
    viewModelIsLocked: boolean;
  }
) => void;

type MoveLogSender = (
  eventName: 'view_model:move',
  params: { workspaceId: WorkspaceId; viewModelId: ViewModelId; folderId: FolderId }
) => void;

type MoveToOtherWorkspaceLogSender = (
  eventName: 'view_model:move_to_other_workspace',
  params: { viewModelId: ViewModelId; sourceWorkspaceId: WorkspaceId; destinationWorkspaceId: WorkspaceId }
) => void;

type DeleteLogSender = (eventName: 'view_model:delete', params: { viewModelId: ViewModelId }) => void;
type RestoreLogSender = (eventName: 'view_model:restore', params: { viewModelId: ViewModelId }) => void;

type CreateFolderLogSender = (
  eventName: 'view_model_folder:create',
  params: { parentFolderId: FolderId; workspaceId: WorkspaceId; folderId: FolderId; folderName: string }
) => void;

type UpdateFolderLogSender = (
  eventName: 'view_model_folder:edit',
  params: { workspaceId: WorkspaceId; folderId: FolderId; folderName: string }
) => void;

type MoveFolderLogSender = (
  eventName: 'view_model_folder:move',
  params: { workspaceId: WorkspaceId; folderId: FolderId; sourceFolderId: FolderId; destinationFolderId: FolderId }
) => void;

type MoveFolderToOtherWorkspaceLogSender = (
  eventName: 'view_model_folder:move_to_other_workspace',
  params: {
    sourceWorkspaceId: WorkspaceId;
    sourceFolderId: FolderId;
    destinationWorkspaceId: WorkspaceId;
    destinationFolderId: FolderId;
  }
) => void;

type DeleteFolderLogSender = (
  eventName: 'view_model_folder:delete',
  params: { workspaceId: WorkspaceId; folderId: FolderId }
) => void;

type DuplicateFolderLogSender = (
  eventName: 'view_model_folder:duplicate',
  params: { workspaceId: WorkspaceId; sourceFolderId: FolderId; destinationFolderId: FolderId }
) => void;

export class ViewModelOperation {
  /**
   * 指定フォルダに対してビューモデルを作成する
   * @param workspaceId
   * @param folderId
   * @param name
   * @param logSender ビューモデル作成イベントログの送信関数。ログ記録が不要の場合には null を指定する。
   */
  static async create(
    workspaceId: WorkspaceId,
    folderId: FolderId,
    name: string,
    logSender: CreateLogSender
  ): Promise<ViewModelId> {
    const workspace = await new ObjectRepository(Workspace, RTDBPath.Workspace.workspaceEntityPath(workspaceId)).get();
    if (!workspace) {
      throw new Error(`Workspace not found. workspaceId=${workspaceId}`);
    }
    const groupId = workspace.groupId;

    const entity = ViewModelEntity.buildNew({ groupId, workspaceId, name });
    const viewModelId = entity.id;

    // ビューモデルエンティティを保存し、フォルダツリーに登録する
    await new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).save(entity);
    const rootFolderTreeRepository = new RootFolderTreeRepository(workspaceId);
    await rootFolderTreeRepository.addViewModel(folderId, viewModelId);

    logSender('view_model:create', { viewModelId, viewModelName: name });

    return viewModelId;
  }

  /**
   * 指定ワークスペースのルート階層に対してビューモデルを作成する
   * @param workspaceId
   * @param name
   * @param logSender
   * @returns
   */
  static async createToRoot(workspaceId: WorkspaceId, name: string, logSender: CreateLogSender): Promise<ViewModelId> {
    return await this.create(workspaceId, Folder.ROOT_ID, name, logSender);
  }

  /**
   * 指定のビューモデルの名前、URL共有の設定を更新する
   */
  static async update(
    viewModelId: ViewModelId,
    name: string,
    sharingUserRole: SharingUserRoleJSON,
    isLocked: boolean,
    logSender: UpdateLogSender
  ): Promise<void> {
    await RefBuilder.ref(RTDBPath.ViewModel.viewModelPath(viewModelId)).update({
      name,
      sharingUserRole,
      isLocked,
      updatedAt: ServerValue.TIMESTAMP,
    });

    logSender('view_model:edit', {
      viewModelId,
      viewModelName: name,
      viewModelShareSetting: sharingUserRole,
      viewModelIsLocked: isLocked,
    });
  }

  /**
   * ビューモデルを指定フォルダ配下に移動させる
   * @param workspaceId 移動対象のビューモデルの所属するワークスペースID
   * @param viewModelId 移動対象のビューモデル
   * @param folderId 移動先のフォルダID
   * @param logSender
   */
  static async move(
    workspaceId: WorkspaceId,
    viewModelId: ViewModelId,
    folderId: FolderId,
    logSender: MoveLogSender
  ): Promise<void> {
    const repo = new RootFolderTreeRepository(workspaceId);
    await repo.moveViewModel(viewModelId, folderId);

    logSender('view_model:move', { workspaceId, viewModelId, folderId });
  }

  /**
   * ビューモデルをワークスペースを跨いで移動させる
   * @param viewModelId 移動対象のビューモデルID
   * @param destinationWorkspaceId 移動先のワークスペースID
   * @param logSender
   * @returns
   */
  static async moveToOtherWorkspace(
    viewModelId: ViewModelId,
    destinationWorkspaceId: WorkspaceId,
    logSender: MoveToOtherWorkspaceLogSender
  ): Promise<boolean> {
    const entity = await new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).get();

    if (!entity) {
      console.warn(`ViewModelEntity not found. id: ${viewModelId}`);
      return false;
    }

    const sourceWorkspaceId = entity.workspaceId;

    try {
      // ビューモデル・エンティティの workspaceId プロパティを更新
      await RefBuilder.ref(RTDBPath.ViewModel.viewModelWorkspaceIdPath(viewModelId)).set(destinationWorkspaceId);

      // 移動元のフォルダツリーから削除する
      const sourceTree = new RootFolderTreeRepository(sourceWorkspaceId);
      await sourceTree.removeViewModel(viewModelId);

      // 移動先のフォルダツリーに追加する
      const destinationTree = new RootFolderTreeRepository(destinationWorkspaceId);
      await destinationTree.addViewModel(Folder.ROOT_ID, viewModelId);

      logSender('view_model:move_to_other_workspace', {
        sourceWorkspaceId,
        destinationWorkspaceId,
        viewModelId,
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * 指定されたビューモデルを論理削除する
   * @param viewModelId
   */
  static async delete(viewModelId: ViewModelId, logSender: DeleteLogSender): Promise<void> {
    const entity = await new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).get();
    if (!entity) {
      throw new Error(`ViewModel(id=${viewModelId}) is not found.`);
    }

    await RefBuilder.ref(RTDBPath.ViewModel.trashedAtPath(viewModelId)).set(ServerValue.TIMESTAMP);

    const rootFolderTreeRepository = new RootFolderTreeRepository(entity.workspaceId);
    await rootFolderTreeRepository.removeViewModel(viewModelId);

    logSender('view_model:delete', { viewModelId });
  }

  /**
   * 指定されたビューモデルを削除済み状態から復元する
   */
  static async restore(viewModelId: ViewModelId, logSender: RestoreLogSender): Promise<void> {
    const entity = await new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).get();
    if (!entity) {
      throw new Error(`ViewModel(id=${viewModelId}) is not found.`);
    }

    await RefBuilder.ref(RTDBPath.ViewModel.trashedAtPath(viewModelId)).set(null);

    const rootFolderTreeRepository = new RootFolderTreeRepository(entity.workspaceId);
    await rootFolderTreeRepository.addViewModel(Folder.ROOT_ID, viewModelId);

    logSender('view_model:restore', { viewModelId });
  }

  /**
   * 指定のビューモデルに対して個別権限を設定する
   * TODO: EPIC-356 バックエンドに処理を移してそれを呼び出す形に変更する
   */
  static async addIndividualPermissions(
    viewModelId: ViewModelId,
    userIds: UserId[],
    role: ViewModelIndividualPermissionRole
  ): Promise<void> {
    const values = Object.fromEntries(
      userIds.map((userId) => [RTDBPath.ViewModel.viewModelIndividualPermissionPath(viewModelId, userId), role])
    );
    await RefBuilder.ref().update(values);
  }

  /**
   * 指定のビューモデルの個別権限を更新する
   * TODO: EPIC-356 バックエンドに処理を移してそれを呼び出す形に変更する
   */
  static async updateIndividualPermission(
    viewModelId: ViewModelId,
    userId: UserId,
    role: ViewModelIndividualPermissionRole
  ): Promise<void> {
    await RefBuilder.ref(RTDBPath.ViewModel.viewModelIndividualPermissionPath(viewModelId, userId)).set(role);
  }

  /**
   * 指定のビューモデルの個別権限を削除する
   * TODO: EPIC-356 バックエンドに処理を移してそれを呼び出す形に変更する
   */
  static async removeIndividualPermissions(viewModelId: ViewModelId, userIds: UserId[]): Promise<void> {
    await Promise.all(
      userIds.map(async (userId) => {
        await RefBuilder.ref(RTDBPath.ViewModel.viewModelIndividualPermissionPath(viewModelId, userId)).set(null);
      })
    );
  }

  /**
   * フォルダを作成する
   */
  static async createFolder(
    workspaceId: WorkspaceId,
    parentFolderId: FolderId,
    name: string,
    logSender: CreateFolderLogSender
  ): Promise<FolderId> {
    const folder = Folder.buildNew(name, workspaceId);
    const rootFolderTreeRepo = new RootFolderTreeRepository(workspaceId);

    await RefBuilder.ref(RTDBPath.Workspace.folderPath(folder.id)).set(folder.dump());
    await rootFolderTreeRepo.addFolder(parentFolderId, folder.id);

    logSender('view_model_folder:create', {
      workspaceId,
      parentFolderId,
      folderId: folder.id,
      folderName: folder.name,
    });

    return folder.id;
  }

  /**
   * フォルダを更新する
   */
  static async updateFolder(folderId: FolderId, name: string, logSender: UpdateFolderLogSender): Promise<void> {
    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    const folder = await repo.get();
    if (!folder) {
      throw new Error(`Folder(id=${folderId}) does not found.`);
    }

    await repo.save(folder.withName(name));

    logSender('view_model_folder:edit', {
      folderId,
      folderName: name,
      workspaceId: folder.workspaceId,
    });
  }

  /**
   * フォルダを移動先フォルダ配下に移動する
   */
  static async moveFolder(
    folderId: FolderId,
    destinationFolderId: FolderId,
    logSender: MoveFolderLogSender
  ): Promise<void> {
    if (folderId === destinationFolderId) {
      throw new Error(`Folder(id=${folderId}) and Destination Folder(id=${destinationFolderId}) are the same.`);
    }

    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    const folder = await repo.get();
    if (!folder) {
      throw new Error(`Folder(id=${folderId}) does not found.`);
    }

    const treeRepo = new RootFolderTreeRepository(folder.workspaceId);
    const tree = await treeRepo.load();
    if (!tree.has(folderId)) {
      throw new Error(`Folder(id=${folderId}) doest not exists in RootFolderTree(workspaceId=${folder.workspaceId})`);
    }

    const sourceFolderId = tree.getParentFolderId(folder.id);
    if (!sourceFolderId) {
      throw new Error(
        `The parent folder of Folder(id=${folder.id}) does not found in RootFolderTree(workspaceId=${folder.workspaceId})`
      );
    }

    const destinationFolder = tree.findFolder(destinationFolderId);
    if (!destinationFolder) {
      throw new Error(`The destination folder (id=${destinationFolderId}) doest not found.`);
    }

    // 指定したフォルダの親フォルダを destinationFolderId に変更する
    await treeRepo.moveFolder(folderId, destinationFolderId);

    logSender('view_model_folder:move', {
      workspaceId: folder.workspaceId,
      folderId: folder.id,
      sourceFolderId,
      destinationFolderId,
    });
  }

  /**
   * フォルダとその中身を別ワークスペースに移動する
   */
  static async moveFolderToOtherWorkspace(
    folderId: FolderId,
    destinationWorkspaceId: WorkspaceId,
    logSender: MoveFolderToOtherWorkspaceLogSender
  ): Promise<void> {
    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    const folder = await repo.get();
    if (!folder) {
      throw new Error(`Folder(id=${folderId}) does not found.`);
    }

    const treeRepo = new RootFolderTreeRepository(folder.workspaceId);
    const rootTree = await treeRepo.load();
    const tree = rootTree.findFolder(folderId);
    if (!tree) {
      throw new Error(`Folder(id=${folderId}) doest not exists in RootFolderTree(workspaceId=${folder.workspaceId})`);
    }

    // 移動先ワークスペースのルート階層にフォルダを作成する
    const destinationFolderId = await this.createFolder(
      destinationWorkspaceId,
      Folder.ROOT_ID,
      folder.name,
      () => void 0
    );

    // 移動対象フォルダ配下のビューモデルとフォルダを再起的に移動する
    // (ビューモデルはIDを変更せずにワークスペースを跨いで移動し、フォルダは移動先ワークスペース配下に新たに作成する)
    await tree.cloneWith(
      destinationFolderId,
      async (viewModelId, destinationFolderId) => {
        await this.moveToOtherWorkspace(viewModelId, destinationWorkspaceId, () => void 0);
        await this.move(destinationWorkspaceId, viewModelId, destinationFolderId, () => void 0);
        return viewModelId;
      },
      async (sourceFolderId, destinationFolderId) => this.cloneFolder(sourceFolderId, destinationFolderId)
    );

    // 移動元のフォルダを削除する
    await this.deleteFolder(folderId, () => void 0);

    logSender('view_model_folder:move_to_other_workspace', {
      sourceWorkspaceId: folder.workspaceId,
      sourceFolderId: folderId,
      destinationWorkspaceId,
      destinationFolderId,
    });
  }
  /**
   * フォルダを削除する
   */
  static async deleteFolder(folderId: FolderId, logSender: DeleteFolderLogSender): Promise<void> {
    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    const folder = await repo.get();
    if (!folder) {
      throw new Error(`Folder(id=${folderId}) does not found.`);
    }

    const treeRepo = new RootFolderTreeRepository(folder.workspaceId);
    const tree = await treeRepo.load();
    if (!tree.has(folderId)) {
      throw new Error(`Folder(id=${folderId}) doest not exists in RootFolderTree(workspaceId=${folder.workspaceId})`);
    }

    await Promise.all(tree.getViewModelIdsOf(folderId).map(async (id) => this.delete(id, () => void 0)));

    await Promise.all(tree.getFolderIdsOf(folderId).map(async (id) => this.deleteFolder(id, () => void 0)));

    await treeRepo.removeFolder(folderId);
    await repo.delete();

    logSender('view_model_folder:delete', {
      folderId,
      workspaceId: folder.workspaceId,
    });
  }

  /**
   * 指定したフォルダとその中身を複製する
   */
  static async duplicateFolder(
    folderId: FolderId,
    currentUserId: UserId,
    onError: (message: string) => void,
    logSender: DuplicateFolderLogSender
  ): Promise<FolderId> {
    const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId));
    const folder = await repo.get();
    if (!folder) {
      throw new Error(`Folder(id=${folderId}) does not found.`);
    }

    const rootTreeRepo = new RootFolderTreeRepository(folder.workspaceId);
    const rootTree = await rootTreeRepo.load();
    const parentFolderId = rootTree.getParentFolderId(folderId);
    const tree = rootTree.findFolder(folderId);
    if (!parentFolderId || !tree) {
      throw new Error(`Folder(id=${folderId}) doest not exists in RootFolderTree(workspaceId=${folder.workspaceId})`);
    }

    // フォルダを複製する
    const newFolderId = await this.createFolder(
      folder.workspaceId,
      parentFolderId,
      `${folder.name}のコピー`,
      () => void 0
    );

    // 複製対象フォルダ配下のフォルダとビューモデルを全て複製する
    await tree.cloneWith(
      newFolderId,
      (sourceViewModelId, destinationFolderId) =>
        this.cloneViewModel(sourceViewModelId, destinationFolderId, currentUserId, onError),
      (sourceFolderId, destinationFolderId) => this.cloneFolder(sourceFolderId, destinationFolderId)
    );

    logSender('view_model_folder:duplicate', {
      workspaceId: folder.workspaceId,
      sourceFolderId: folderId,
      destinationFolderId: newFolderId,
    });

    return newFolderId;
  }

  private static async cloneFolder(sourceFolderId: FolderId, destinationFolderId: FolderId): Promise<FolderId> {
    const sourceFolder = await new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(sourceFolderId)).get();
    if (!sourceFolder) {
      throw new Error(`Folder(id=${sourceFolderId}) does not found.`);
    }

    const destFolder = await new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(destinationFolderId)).get();
    if (!destFolder) {
      throw new Error(`Folder(id=${destinationFolderId}) does not found.`);
    }

    return await this.createFolder(destFolder.workspaceId, destFolder.id, sourceFolder.name, () => void 0);
  }

  private static async cloneViewModel(
    sourceViewModelId: ViewModelId,
    destinationFolderId: FolderId,
    currentUserId: UserId,
    onError: (message: string) => void
  ): Promise<ViewModelId> {
    const entity = await new ObjectRepository(
      ViewModelEntity,
      RTDBPath.ViewModel.viewModelPath(sourceViewModelId)
    ).get();
    if (!entity) {
      throw new Error(`ViewModel(id=${sourceViewModelId}) does not found.`);
    }

    const content = await new ViewModelCascadeRepository(sourceViewModelId).loadContent();
    if (!content) {
      throw new Error(`複製元のビューモデル・コンテンツの読み取りに失敗しました`);
    }

    const payload = ViewModelClipboardPayload.fromContent(content, entity.workspaceId, sourceViewModelId);

    const viewModelId = await this.create(entity.workspaceId, destinationFolderId, payload.name, () => void 0);

    const errorMessage = await ViewModelClipboardOperator.pasteWithoutCommand(
      entity.groupId,
      viewModelId,
      currentUserId,
      payload
    );

    if (errorMessage) {
      onError(errorMessage);
    }

    return viewModelId;
  }
}
